import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useStateScope } from '../context/StateScopeContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Users, Gift, TrendingUp, Award, Globe, Coins, CheckCircle2, Play, FileText, Pen, Lock, X, Pause, ChevronDown, ChevronUp, Shield, Video, Phone, Users2, BookOpen, MessageSquare } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from 'recharts';
import { toast } from 'sonner';
import PageLoader from '../components/PageLoader';
import OnboardingCompleteOverlay from '../components/OnboardingCompleteOverlay';
import OnboardingStepSheet from '../components/OnboardingStepSheet';
import IntroStyleVideoPlayer from '../components/IntroStyleVideoPlayer';
import PullToRefresh from '../components/PullToRefresh';
import { getAgreementConfigFor } from './AgreementSigningPage';

// iter166 — Two-letter codes for the lead-state step_ids stored in
// system_settings.passwords (e.g. step3_lead_NC, step3_lead_CA…).
// Mirrors the keys used by the admin Passwords config.
const STATE_CODES = {
  'North Carolina': 'NC', 'South Carolina': 'NC',
  'California': 'CA',
  'New York': 'NY',
  'District of Columbia': 'DC', 'Virginia': 'DC',
  'Massachusetts': 'MA',
  'Illinois': 'IL',
  'Pennsylvania': 'PA', 'Maryland': 'PA',
  'Georgia': 'GA',
  'Florida': 'FL',
  'Michigan': 'MI', 'Ohio': 'MI',
  'Tennessee': 'TN', 'Kentucky': 'TN',
  'Washington': 'WA', 'Oregon': 'WA',
};
const step3MetaIdFor = (role, state) => {
  const r = (role || '').toLowerCase();
  if (!r || r === 'admin') return null;
  if (r === 'lead') {
    const code = STATE_CODES[state] || 'All';
    return `step3_lead_${code}`;
  }
  return `step3_${r}`;
};

// Intro Video Player Component
const IntroVideoPlayer = ({ onComplete, onClose, videoSrc }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const [maxWatchedTime, setMaxWatchedTime] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const INTRO_VIDEO_URL = videoSrc || "/intro_video.mp4";

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const handleVideoEnd = () => {
    setVideoEnded(true);
    setIsPlaying(false);
    onComplete();
  };

  const handleClose = () => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    if (!videoEnded) {
      toast.info('Complete the video to mark Step 1 as done');
    }
    onClose();
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const video = videoRef.current;
      setCurrentTime(video.currentTime);
      if (video.currentTime > maxWatchedTime) {
        setMaxWatchedTime(video.currentTime);
      }
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleSeeking = () => {
    if (videoRef.current) {
      const video = videoRef.current;
      if (video.currentTime > maxWatchedTime + 0.5) {
        video.currentTime = maxWatchedTime;
        toast.warning('Please watch the video without skipping');
      }
    }
  };

  const remainingTime = Math.max(0, duration - currentTime);

  return (
    <div className="fixed inset-0 bg-black z-[100] flex items-center justify-center">
      <div className="relative w-full max-w-md mx-4">
        <div className="absolute top-4 right-4 z-10 flex gap-2">
          <Button
            variant="ghost"
            className="bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 h-auto"
            onClick={handlePause}
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </Button>
          <Button
            variant="ghost"
            className="bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 h-auto"
            onClick={handleClose}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Countdown Timer */}
        {duration > 0 && !videoEnded && (
          <div className="absolute top-4 left-4 z-10 bg-black/60 px-3 py-1 rounded-full">
            <span className="text-white text-sm font-medium">
              {formatTime(remainingTime)} remaining
            </span>
          </div>
        )}
        
        <video
          ref={videoRef}
          className="w-full rounded-lg"
          onEnded={handleVideoEnd}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onSeeking={handleSeeking}
          playsInline
        >
          <source src={INTRO_VIDEO_URL} type="video/mp4" />
        </video>
        
        {videoEnded && (
          <div className="absolute inset-0 bg-black/70 flex items-center justify-center rounded-lg">
            <div className="text-center text-white">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Step 1 Complete!</h3>
              <p className="text-gray-300 mb-4">Great job! Continue with your onboarding.</p>
              <Button onClick={handleClose} className="bg-[#757150] hover:bg-[#8a8560]">Continue</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Step 2 Sub-steps — iter 96 reorder/rename per user spec:
// • Step 2A "Submit Application" is gated by the signup-time
//   application password (set in admin onboarding) and is therefore
//   pre-checked for every newly-created user. We KEEP the data
//   field for back-compat but no longer expose it as an interactive
//   row (see VISIBLE_SUB_STEPS below).
// • Step 2F (Peer Interview) and 2G (Join Intranet) are removed.
// • Labels updated: Leadership Interview → "We interview you";
//   Live Q&A → "You interview us". Step prefixes dropped from UI.
const step2SubSteps = [
  { key: 'step2a_application_submitted', label: 'Submit Application', icon: FileText, description: 'Submit your application', route: '/submit-application' },
  { key: 'step2b_identity_verified', label: 'Verify your Identity', icon: Shield, description: 'Verify your identity', route: '/identity-verification' },
  { key: 'step2c_nda_signed', label: 'Sign NDA', icon: FileText, description: 'Sign NDA', route: '/nda' },
  { key: 'step2d_mfa_activated', label: 'Activate MFA', icon: Lock, description: 'Enable multi-factor authentication', route: '/mfa' },
  { key: 'step2e_leadership_interview', label: 'Undergo an Interview', icon: Video, description: 'We interview you', route: '/leadership-interview' },
  { key: 'step2h_how_orb_works', label: 'Learn how ORB works', icon: BookOpen, description: 'Training video', route: '/how-orb-works' },
  { key: 'step2i_role_duties', label: 'Learn the role & duties', icon: BookOpen, description: 'Training video', route: null },
  { key: 'step2j_live_qa', label: 'Interview our team', icon: MessageSquare, description: 'You interview us', route: null },
];

// What the user actually SEES on their dashboard. Submit Application
// is auto-completed at signup so we hide it from the visible list to
// avoid a permanently-greyed row that confuses users.
const VISIBLE_SUB_STEPS = step2SubSteps.filter(s => s.key !== 'step2a_application_submitted');

// Onboarding Dashboard for non-onboarded users
const OnboardingDashboard = ({ user }) => {
  const { updateUser, refreshUser, api } = useAuth();
  const { brandLabel } = useStateScope();
  const navigate = useNavigate();
  const [showVideo, setShowVideo] = useState(false);
  const [step2Expanded, setStep2Expanded] = useState(false);
  const [showAgreementDialog, setShowAgreementDialog] = useState(false);
  const [agreementPassword, setAgreementPassword] = useState('');

  // iter164 — Step meta (link, access_password, bg_color, access_enabled)
  // keyed by step_id (step2a, step2c, step3_supervisor, etc.). Loaded
  // once on mount from /me/onboarding/step-meta.
  const [stepMeta, setStepMeta] = useState({});
  const [activeSheet, setActiveSheet] = useState(null); // {step_id, label, route, key}
  // iter166 — When the user closes the NDA popup without finishing,
  // we surface an inline "Enter Password" prompt on the NDA sub-step
  // row of the home page. This is the home-page mirror of the
  // standalone /nda page's "I've already signed - enter password"
  // affordance, brought up to the home dashboard so the user doesn't
  // have to re-open the popup to advance once they have the password.
  // Tracks {key, password, verifying, error}.
  const [pwdPrompt, setPwdPrompt] = useState(null);

  // iter174 — Global onboarding videos (intro / step2h / step2i).
  // Admin can upload an override via /admin/onboarding/videos which
  // is what the user-facing players prefer over the static
  // /intro_video.mp4 / /step2h_video.mp4 / /step2i_video.mp4 paths
  // (the static files are too large to ship through the production
  // CDN reliably). Returns { intro_video:{url}, step2h_video:{url},
  // step2i_video:{url} }.
  const [globalVideos, setGlobalVideos] = useState({});
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/me/onboarding/videos');
        if (!cancelled) setGlobalVideos(r.data || {});
      } catch (_e) { /* fallback to static paths */ }
    })();
    return () => { cancelled = true; };
  }, [api]);
  const introVideoSrc  = globalVideos?.intro_video?.url  || '/intro_video.mp4';
  const step2hVideoSrc = globalVideos?.step2h_video?.url || '/step2h_video.mp4';
  const step2iVideoSrc = globalVideos?.step2i_video?.url || '/step2i_video.mp4';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/me/onboarding/step-meta');
        if (!cancelled) setStepMeta(r.data || {});
      } catch (e) { /* leave empty */ }
    })();
    return () => { cancelled = true; };
  }, [api]);

  // Resolve the step_id used in the passwords/meta doc for a given
  // sub-step's key. e.g. step2a_application_submitted → 'step2a'.
  const subStepMetaId = (subStepKey) => {
    const m = subStepKey.match(/^(step2[a-z])_/);
    if (m) return m[1];
    if (subStepKey === 'intro_video_watched') return 'step1';
    return subStepKey;
  };

  // iter164 — Resolve the popup's iframe src for a given sub-step.
  // Internal routes are rendered via `?embedded=1` flag (consumed by
  // `useEmbeddedMode` on each step page) so we get the existing page
  // logic + the new popup chrome. Admin-configured external URLs
  // (from /onboarding/step-meta) take precedence ONLY when the step
  // page is not internally routable.
  const resolveSheetSrc = (subStep, meta) => {
    if (subStep?.route) {
      // Carry the embedded flag + any existing query params on the route
      const join = subStep.route.includes('?') ? '&' : '?';
      return `${subStep.route}${join}embedded=1`;
    }
    return meta?.link || '';
  };

  // iter164 — Listen for completion postMessages bubbling up from
  // the embedded iframe. The child page calls
  // postEmbeddedStepComplete(stepKey) on success. We close the popup
  // and refresh the user; the auto-advance cascade already fires
  // server-side, so the homepage's existing `wasOnboarding → !needsOnboarding`
  // edge handler will display the celebration overlay automatically.
  useEffect(() => {
    const onMsg = async (e) => {
      if (e?.origin !== window.location.origin) return;
      if (e?.data?.type !== 'onboarding:step-complete') return;
      try { await refreshUser(); } catch (_e) { /* ignore */ }
      setActiveSheet(null);
      setPwdPrompt(null);
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [refreshUser]);

  // Visible-list completion (what the user sees) is what drives
  // the "X / Y" tally and the per-row check icons. The all-complete
  // gate uses the FULL list (including the auto-checked Submit
  // Application) so legacy data flips `is_onboarded` correctly.
  const step2VisibleCount = VISIBLE_SUB_STEPS.filter(s => user?.[s.key]).length;
  const step2CompletedCount = step2SubSteps.filter(s => user?.[s.key]).length;
  const allStep2Complete = step2CompletedCount === step2SubSteps.length;

  const handleVideoComplete = async () => {
    try {
      await updateUser({ intro_video_watched: true });
      await refreshUser();
      toast.success('Step 1 complete!');
    } catch (error) {
      console.error('Failed to update video status:', error);
    }
  };

  const handleSubStepClick = async (subStep) => {
    // iter164 — Every sub-step opens as a popup rising from the bottom
    // (4/5 viewport, rounded top, drag-to-close). The popup body
    // renders the existing step page in an embedded iframe so we
    // get the same content with one consistent visual wrapper.
    // Clear any stale inline password prompt for a different step.
    setPwdPrompt(null);
    // iter166 — Re-fetch the step-meta BEFORE opening so admin
    // changes to bg_color / link / access password propagate
    // immediately without requiring a full page reload. Previously
    // we cached step-meta on mount, which is why admins reported
    // "the background color never changes" — they were always
    // seeing the value from when the page first loaded.
    try {
      const r = await api.get('/me/onboarding/step-meta');
      setStepMeta(r.data || {});
    } catch (_e) { /* keep cached value if refresh fails */ }
    setActiveSheet(subStep);
  };

  // iter166 — Steps that ask the user for a confirmation password
  // AFTER signing/finishing an external form. When the popup is
  // closed without the iframe posting completion, we surface the
  // inline "Enter Password" prompt on the home page for these.
  //   - step2c_nda_signed → uses /auth/verify-onboarding-password
  //   - agreement_signed (Step 3) → uses /agreement/verify
  const PWD_GATED_KEYS = new Set(['step2c_nda_signed', 'agreement_signed']);

  // Map sub-step key → backend `step_id` used by /auth/verify-onboarding-password.
  const stepIdForKey = (key) => {
    const m = key.match(/^(step2[a-z])_/);
    return m ? m[1] : key;
  };

  // iter166 — When the user closes a password-gated popup (e.g. NDA)
  // without finishing, surface the inline password prompt on that
  // row. If the user re-opens the popup or the step is already
  // complete, the prompt is cleared elsewhere.
  const handleSheetClose = () => {
    const sheet = activeSheet;
    setActiveSheet(null);
    if (!sheet) return;
    const alreadyDone = !!user?.[sheet.key];
    if (!alreadyDone && PWD_GATED_KEYS.has(sheet.key)) {
      setPwdPrompt({ key: sheet.key, password: '', verifying: false });
    }
  };

  // iter166 — Verify the confirmation password from the home-page
  // inline prompt. Mirrors NDAPage's `handleVerifyPassword` so users
  // can complete the step without re-opening the popup. For the
  // Step-3 agreement, hits the dedicated /agreement/verify endpoint
  // (which also enforces server-side that Step 2 is complete).
  const handleVerifyInlinePassword = async () => {
    if (!pwdPrompt?.password) {
      toast.error('Please enter the password');
      return;
    }
    setPwdPrompt((p) => ({ ...p, verifying: true }));
    try {
      if (pwdPrompt.key === 'agreement_signed') {
        const res = await api.post('/agreement/verify', {
          confirmation_code: pwdPrompt.password.trim(),
        });
        if (res.data?.success || res.data?.is_onboarded) {
          await refreshUser();
          toast.success('Agreement signed — you are now fully onboarded!');
          setPwdPrompt(null);
        } else {
          toast.error('Incorrect code.');
          setPwdPrompt((p) => ({ ...p, verifying: false }));
        }
      } else {
        const res = await api.post('/auth/verify-onboarding-password', {
          step_id: stepIdForKey(pwdPrompt.key),
          password: pwdPrompt.password,
        });
        if (res.data?.valid) {
          await updateUser({ [pwdPrompt.key]: true });
          await refreshUser();
          toast.success('Step completed!');
          setPwdPrompt(null);
        } else {
          toast.error('Incorrect password.');
          setPwdPrompt((p) => ({ ...p, verifying: false }));
        }
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not verify. Please try again.';
      toast.error(msg);
      setPwdPrompt((p) => ({ ...p, verifying: false }));
    }
  };

  const handleSignAgreement = async () => {
    if (!agreementPassword) {
      toast.error('Please enter your password');
      return;
    }
    // iter129 — Signing the agreement is now the FINAL step. The
    // backend auto-flips is_onboarded on this write so refreshUser()
    // immediately routes us to the FullDashboard.
    try {
      await updateUser({ agreement_signed: true });
      await refreshUser();
      toast.success('Welcome aboard! You now have full access.');
      setShowAgreementDialog(false);
    } catch (error) {
      toast.error('Failed to sign agreement');
    }
  };

  const steps = [
    {
      id: 1,
      title: 'Watch Intro Video',
      description: 'Learn about The Uplift and all its features',
      icon: Play,
      completed: user?.intro_video_watched,
      action: () => setShowVideo(true)
    },
    {
      id: 2,
      title: 'Complete Onboarding',
      description: `${step2VisibleCount}/${VISIBLE_SUB_STEPS.length} steps completed`,
      icon: FileText,
      completed: allStep2Complete,
      hasSubSteps: true,
      action: () => setStep2Expanded(!step2Expanded)
    },
    {
      id: 3,
      title: 'Sign Agreement',
      description: 'Review and sign your onboarding agreement',
      icon: Pen,
      completed: user?.agreement_signed,
      // iter164 — Open the agreement in the same universal popup
      // pattern as the rest of Step 2. The agreement page detects
      // ?embedded=1 and renders chrome-less + postMessages
      // 'onboarding:step-complete' on successful signing.
      action: () => setActiveSheet({
        key: 'agreement_signed',
        label: 'Sign Agreement',
        description: 'Review and sign your onboarding agreement',
        route: '/sign-agreement',
      })
    }
  ];

  const completedSteps = [user?.intro_video_watched, allStep2Complete, user?.agreement_signed].filter(Boolean).length;
  const allStepsComplete = completedSteps === 3;

  return (
    <>
      {showVideo && (
        <IntroVideoPlayer 
          onComplete={handleVideoComplete}
          onClose={() => setShowVideo(false)}
          videoSrc={introVideoSrc}
        />
      )}
      
      <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 md:pt-2 pb-24 space-y-6" data-testid="onboarding-dashboard">
        <div className="text-center py-4">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
            Welcome to {brandLabel}, {user?.first_name || user?.full_name?.split(' ')[0]}!
          </h1>
          <p className="text-muted-foreground mt-2">
            Complete the following steps to unlock all features
          </p>
        </div>

        {/* Progress indicator */}
        <Card className="border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Onboarding Progress</span>
              <span className="text-sm text-muted-foreground">{completedSteps} of 3 steps</span>
            </div>
            <Progress value={(completedSteps / 3) * 100} className="h-2" />
          </CardContent>
        </Card>

        {/* Onboarding Steps */}
        <div className="space-y-4">
          {steps.map((step, idx) => {
            const Icon = step.icon;
            const isLocked = idx > 0 && !steps[idx - 1].completed;
            const isStep2 = step.id === 2;
            const isStep3 = step.id === 3;
            // iter166 — inline confirmation-code prompt under Step 3
            // when the user closed the agreement popup without
            // finishing. Mirrors the NDA inline pattern.
            const showStep3InlinePwd = isStep3 && pwdPrompt?.key === 'agreement_signed' && !step.completed && !isLocked;
            // iter166 — Step 3 access password badge: shown directly
            // on the card (same UX as the NDA sub-step badge), gated
            // by the admin's per-step access_enabled toggle. The
            // password value falls back to the role/state-aware
            // AGREEMENT_CONFIG so it works without the admin having
            // to manually re-enter it.
            const step3MetaId = isStep3 ? step3MetaIdFor(user?.role, user?.state) : null;
            const step3Meta = step3MetaId ? (stepMeta[step3MetaId] || {}) : {};
            const step3AccessPw = step3Meta.access_password
              || getAgreementConfigFor(user?.role, user?.state)?.passwordA
              || '';
            const showStep3AccessPw = isStep3 && !step.completed && !isLocked && step3Meta.access_enabled !== false && !!step3AccessPw;
            
            return (
              <div key={step.id}>
                <Card 
                  className={`transition-all ${step.completed ? 'border-green-500/50 bg-green-500/5' : isLocked ? 'opacity-60' : 'hover:shadow-md cursor-pointer'}`}
                  onClick={() => !isLocked && !step.completed && !showStep3InlinePwd && step.action()}
                  data-testid={`onboarding-step-${step.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        step.completed 
                          ? 'bg-green-500 text-white' 
                          : isLocked 
                            ? 'bg-muted text-muted-foreground' 
                            : 'bg-primary/10 text-primary'
                      }`}>
                        {step.completed ? (
                          <CheckCircle2 className="w-6 h-6" />
                        ) : isLocked ? (
                          <Lock className="w-5 h-5" />
                        ) : (
                          <Icon className="w-6 h-6" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold flex items-center gap-2">
                          Step {step.id}: {step.title}
                          {step.completed && (
                            <Badge variant="default" className="bg-green-500 text-xs">Complete</Badge>
                          )}
                        </h3>
                        <p className="text-sm text-muted-foreground">{step.description}</p>
                        {showStep3AccessPw && (
                          <p
                            className="text-[11px] mt-1 font-mono text-amber-700 dark:text-amber-400"
                            data-testid="step3-access-pw"
                          >
                            Access: <span className="font-semibold">{step3AccessPw}</span>
                          </p>
                        )}
                      </div>
                      {isStep2 && !isLocked && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={(e) => { e.stopPropagation(); setStep2Expanded(!step2Expanded); }}
                        >
                          {step2Expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </Button>
                      )}
                      {!step.completed && !isLocked && !isStep2 && !showStep3InlinePwd && (
                        <Button variant="outline" size="sm">
                          Start
                        </Button>
                      )}
                    </div>

                    {/* iter166 — Inline confirmation-code prompt for
                        Step 3. Surfaces when the user closes the
                        Sign-Agreement popup without finishing. Hits
                        the dedicated /agreement/verify endpoint. */}
                    {showStep3InlinePwd && (
                      <div
                        className="mt-3 pt-3 border-t border-border/60"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="step3-inline-pwd"
                      >
                        <p className="text-xs text-muted-foreground mb-2">
                          Enter the confirmation code from the agreement form to complete onboarding.
                        </p>
                        <div className="flex gap-2">
                          <Input
                            type="text"
                            autoFocus
                            placeholder="Confirmation code"
                            value={pwdPrompt.password}
                            onChange={(e) => setPwdPrompt((p) => ({ ...p, password: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleVerifyInlinePassword(); }}
                            className="h-9 text-sm"
                            data-testid="step3-inline-pwd-input"
                          />
                          <Button
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); handleVerifyInlinePassword(); }}
                            disabled={pwdPrompt.verifying || !pwdPrompt.password}
                            className="h-9"
                            data-testid="step3-inline-pwd-submit"
                          >
                            {pwdPrompt.verifying ? 'Verifying…' : 'Enter'}
                          </Button>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setPwdPrompt(null); step.action(); }}
                          className="mt-2 text-[11px] text-muted-foreground hover:text-foreground underline"
                          data-testid="step3-inline-pwd-reopen"
                        >
                          Re-open the agreement
                        </button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Step 2 Sub-steps */}
                {isStep2 && step2Expanded && !isLocked && (
                  <div className="ml-6 mt-2 space-y-2 border-l-2 border-primary/20 pl-4">
                    {VISIBLE_SUB_STEPS.map((subStep, subIdx) => {
                      const SubIcon = subStep.icon;
                      // Sequential locking: first sub-step is always unlocked (when Step 2 is accessible)
                      // Subsequent sub-steps require the previous one to be complete
                      const isSubLocked = subIdx > 0 && !user?.[VISIBLE_SUB_STEPS[subIdx - 1].key];
                      const isSubComplete = user?.[subStep.key];
                      // iter164 — show the access password on the
                      // card (the page-before-popup) when the admin
                      // has flagged this step as access-required. The
                      // password is NOT shown inside the popup.
                      const meta = stepMeta[subStepMetaId(subStep.key)] || {};
                      const showAccessPw = meta.access_enabled && meta.access_password && !isSubComplete && !isSubLocked;
                      // iter166 — inline password prompt expansion
                      // when the user closed this sub-step's popup
                      // without finishing.
                      const showInlinePwd = pwdPrompt?.key === subStep.key && !isSubComplete && !isSubLocked;

                      return (
                        <Card
                          key={subStep.key}
                          className={`transition-all ${isSubComplete ? 'border-green-500/30 bg-green-500/5' : isSubLocked ? 'opacity-50' : 'hover:shadow-sm cursor-pointer'}`}
                          onClick={() => !isSubLocked && !isSubComplete && !showInlinePwd && handleSubStepClick(subStep)}
                          data-testid={`onboarding-substep-${subStep.key}`}
                        >
                          <CardContent className="p-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                isSubComplete ? 'bg-green-500 text-white' : isSubLocked ? 'bg-muted' : 'bg-primary/10 text-primary'
                              }`}>
                                {isSubComplete ? <CheckCircle2 className="w-4 h-4" /> : isSubLocked ? <Lock className="w-4 h-4" /> : <SubIcon className="w-4 h-4" />}
                              </div>
                              <div className="flex-1">
                                <p className="text-sm font-medium">{subStep.label}</p>
                                <p className="text-xs text-muted-foreground">{subStep.description}</p>
                                {showAccessPw && (
                                  <p
                                    className="text-[11px] mt-1 font-mono text-amber-700 dark:text-amber-400"
                                    data-testid={`substep-access-pw-${subStep.key}`}
                                  >
                                    Access: <span className="font-semibold">{meta.access_password}</span>
                                  </p>
                                )}
                              </div>
                              {isSubComplete && <Badge variant="outline" className="text-xs text-green-600">Done</Badge>}
                              {isSubLocked && <Lock className="w-4 h-4 text-muted-foreground" />}
                            </div>

                            {showInlinePwd && (
                              <div
                                className="mt-3 pt-3 border-t border-border/60"
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`substep-inline-pwd-${subStep.key}`}
                              >
                                <p className="text-xs text-muted-foreground mb-2">
                                  Enter the confirmation password from the NDA page to complete this step.
                                </p>
                                <div className="flex gap-2">
                                  <Input
                                    type="password"
                                    autoFocus
                                    placeholder="Confirmation password"
                                    value={pwdPrompt.password}
                                    onChange={(e) => setPwdPrompt((p) => ({ ...p, password: e.target.value }))}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleVerifyInlinePassword(); }}
                                    className="h-9 text-sm"
                                    data-testid={`substep-inline-pwd-input-${subStep.key}`}
                                  />
                                  <Button
                                    size="sm"
                                    onClick={(e) => { e.stopPropagation(); handleVerifyInlinePassword(); }}
                                    disabled={pwdPrompt.verifying || !pwdPrompt.password}
                                    className="h-9"
                                    data-testid={`substep-inline-pwd-submit-${subStep.key}`}
                                  >
                                    {pwdPrompt.verifying ? 'Verifying…' : 'Enter'}
                                  </Button>
                                </div>
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); setPwdPrompt(null); handleSubStepClick(subStep); }}
                                  className="mt-2 text-[11px] text-muted-foreground hover:text-foreground underline"
                                  data-testid={`substep-inline-pwd-reopen-${subStep.key}`}
                                >
                                  Re-open the form
                                </button>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* iter129 — The "Your onboarding is being reviewed by your
            supervisor" message was removed. Signing the agreement
            now auto-flips `is_onboarded` on the backend, so the
            client refreshUser() immediately routes the user to the
            FullDashboard — no purgatory state. */}
      </div>

      {/* Agreement Dialog */}
      <Dialog open={showAgreementDialog} onOpenChange={setShowAgreementDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sign Onboarding Agreement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-muted/30 rounded-lg max-h-48 overflow-y-auto text-sm">
              <p className="font-semibold mb-2">The Uplift Onboarding Agreement</p>
              <p className="text-muted-foreground">
                By signing this agreement, I acknowledge that I have completed all required onboarding 
                steps and understand my responsibilities as a member of the ORB community. I agree to 
                uphold the values and standards set forth by ORB and to act in accordance with all 
                applicable policies and procedures.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Enter your password to sign</Label>
              <Input
                type="password"
                placeholder="Your password"
                value={agreementPassword}
                onChange={(e) => setAgreementPassword(e.target.value)}
                data-testid="agreement-password-input"
              />
            </div>
            <Button 
              className="w-full" 
              onClick={handleSignAgreement}
              data-testid="sign-agreement-btn"
            >
              <Pen className="w-4 h-4 mr-2" />
              Sign Agreement
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* iter164/iter166 — Universal onboarding step popup. Rises
          from the bottom, drag handle, admin's per-step bg color
          painted behind. For the training-video sub-step we render
          a 1:1 clone of the BusinessCard "Watch the Pitch" popup
          (PitchStylePopup) — same wrapper, same timed gradient,
          same controls. All other sub-steps continue to use the
          generic OnboardingStepSheet with an iframe body. */}
      {/* iter166 — Training-video sub-steps (Step 2H "Learn how ORB
          works" + Step 2I "Learn the role & duties") render as the
          FULLSCREEN intro-style video player, NOT as a popup. Same
          look, controls, and behavior as "Watch the intro video":
          fixed full-screen black overlay, autoplay, pause/close
          buttons top-right, countdown top-left, linear-watch
          enforcement, "Step Complete!" celebration card on end. */}
      {activeSheet && (activeSheet.key === 'step2h_how_orb_works' || activeSheet.key === 'step2i_role_duties') && (
        <IntroStyleVideoPlayer
          src={activeSheet.key === 'step2h_how_orb_works' ? step2hVideoSrc : step2iVideoSrc}
          completionTitle={`${activeSheet.label} Complete!`}
          completionBody="Great job! Continue with your onboarding."
          closeWarning={`Complete the video to mark "${activeSheet.label}" as done`}
          testId={`video-player-${activeSheet.key}`}
          onComplete={async () => {
            try {
              await updateUser({ [activeSheet.key]: true });
              await refreshUser();
            } catch (_e) { /* noop */ }
          }}
          onClose={async () => {
            // iter166 — Auto-advance ONLY when the very next
            // incomplete sub-step is another training video. We
            // chain video-after-video (step 2h → step 2i) for a
            // smooth viewing flow, but we DO NOT auto-open
            // non-video popups like "Interview our team" — those
            // should require an explicit user click.
            const completedKey = activeSheet.key;
            let fresh = null;
            try { fresh = await refreshUser(); } catch (_e) { /* noop */ }
            const u = fresh || user;
            const VIDEO_KEYS = new Set(['step2h_how_orb_works', 'step2i_role_duties']);
            const next = VISIBLE_SUB_STEPS.find((s) => !u?.[s.key] && s.key !== completedKey);
            if (next && VIDEO_KEYS.has(next.key)) {
              setActiveSheet(next);
            } else {
              setActiveSheet(null);
            }
          }}
        />
      )}
      {activeSheet && activeSheet.key !== 'step2h_how_orb_works' && activeSheet.key !== 'step2i_role_duties' && (() => {
        const meta = stepMeta[subStepMetaId(activeSheet.key)] || {};
        const src = resolveSheetSrc(activeSheet, meta);
        return (
          <OnboardingStepSheet
            open={!!activeSheet}
            onClose={handleSheetClose}
            title={activeSheet.label}
            bgColor={meta.bg_color || ''}
            fullBleed={!!src}
            testId={`onboarding-step-sheet-${activeSheet.key}`}
          >
            {src ? (
              <iframe
                src={src}
                title={activeSheet.label}
                className="w-full h-full border-0 bg-white dark:bg-[#0a0a0a]"
                data-testid="onboarding-step-iframe"
              />
            ) : (
              <div className="rounded-xl border border-dashed p-4 text-sm text-muted-foreground">
                No link configured. Ask an admin to set the URL for this step.
              </div>
            )}
          </OnboardingStepSheet>
        );
      })()}
    </>
  );
};

// Full Dashboard for onboarded users
const FullDashboard = ({ user, metrics, trackers, priorities, isFeatureEnabled }) => {
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  const metricCards = isAdmin ? [
    { key: 'global_onboard_count', label: 'Global Onboard', icon: Globe, color: 'text-primary' },
    { key: 'global_equity_awarded', label: 'Global Equity', icon: Coins, color: 'text-secondary' },
    { key: 'metric3', label: '', icon: null, color: '' },
    { key: 'metric4', label: '', icon: null, color: '' }
  ] : [
    { key: 'onboard_count', label: 'Onboard Count', icon: Users, color: 'text-primary' },
    { key: 'referral_count', label: 'Referral Count', icon: Gift, color: 'text-secondary' },
    { key: 'stocks_awarded', label: 'Total Equity Awarded', icon: TrendingUp, color: 'text-chart-1' },
    { key: 'uplift_score', label: 'Uplift Score', icon: Award, color: 'text-chart-2' }
  ];

  const handlePriorityClick = (task) => {
    // Navigate to tasks page with the task ID to open it in expanded view
    navigate(`/tasks?openTask=${task.id}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] p-4 md:pt-2 pb-24 space-y-6" data-testid="home-dashboard">
      <div className="animate-fade-in">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
          Welcome back, {user?.first_name || user?.full_name?.split(' ')[0]}
        </h1>
        <p className="text-muted-foreground mt-1">Here's your performance overview</p>
      </div>

      {/* Key Metrics - Only show if feature is enabled */}
      {isFeatureEnabled('home_key_metrics') && (
        <section className="space-y-3" data-testid="home-key-metrics-section">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Key Metrics</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {metricCards.map((metric, idx) => {
              if (!metric.icon) {
                return (
                  <Card key={idx} className="bg-muted/30 border-dashed">
                    <CardContent className="p-4 h-24 flex items-center justify-center">
                      <span className="text-muted-foreground text-sm">Coming Soon</span>
                    </CardContent>
                  </Card>
                );
              }
              const Icon = metric.icon;
              const value = metrics?.[metric.key] ?? 0;
              return (
                <Card key={metric.key} className={`animate-slide-up stagger-${idx + 1} hover:shadow-md transition-shadow`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Icon className={`w-5 h-5 ${metric.color}`} />
                      <Badge variant="secondary" className="text-xs">{user?.role}</Badge>
                    </div>
                    <p className="text-2xl md:text-3xl font-bold">{value.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground mt-1">{metric.label}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* Key Priorities - Only show if feature is enabled */}
      {isFeatureEnabled('home_key_priorities') && (
        <section className="space-y-3" data-testid="home-key-priorities-section">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Key Priorities</h2>
          {priorities.length === 0 ? (
            <div className="bg-white dark:bg-[#1a1a1a] rounded-lg shadow-sm border border-gray-100 dark:border-gray-800 p-6 text-center text-muted-foreground">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No pending tasks. Great job!</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {priorities.map((task) => (
                <div 
                  key={task.id} 
                  className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-[#1a1a1a] rounded-lg shadow-sm border border-gray-100 dark:border-gray-800 cursor-pointer hover:shadow-md transition-all"
                  onClick={() => handlePriorityClick(task)}
                  data-testid={`priority-${task.id}`}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                    task.status === 'completed' ? 'bg-green-500/20' : 
                    task.status === 'in_progress' ? 'bg-yellow-500/20' : 'bg-primary/20'
                  }`}>
                    <div className={`w-3 h-3 rounded-full ${
                      task.status === 'completed' ? 'bg-green-500' : 
                      task.status === 'in_progress' ? 'bg-yellow-500' : 'bg-primary'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{task.name}</p>
                    {task.deadline && (
                      <p className="text-xs text-muted-foreground">
                        Due: {new Date(task.deadline).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <Badge variant={task.assigned_to ? 'default' : 'outline'} className="text-[10px] px-1.5 py-0 flex-shrink-0">
                    {task.assigned_to ? 'Assigned' : 'Personal'}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Trackers - Only show if feature is enabled */}
      {isFeatureEnabled('home_key_trackers') && (
        <section className="space-y-3" data-testid="home-key-trackers-section">
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Key Trackers</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium">{trackers?.tracker1?.name || 'Weekly Progress'}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3 mb-4">
                  <Progress value={trackers?.tracker1?.current || 0} className="flex-1 h-2" />
                  <span className="text-sm font-medium">{trackers?.tracker1?.current || 0}%</span>
                </div>
                <div className="h-32">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={trackers?.tracker1?.data || []}>
                      <XAxis dataKey="day" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                      <YAxis hide />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {(trackers?.tracker1?.data || []).map((_, index) => (
                          <Cell key={`cell-${index}`} fill={`hsl(var(--primary) / ${0.4 + (index * 0.1)})`} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium">{trackers?.tracker2?.name || 'Monthly Goals'}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3 mb-4">
                  <Progress value={trackers?.tracker2?.current || 0} className="flex-1 h-2" />
                  <span className="text-sm font-medium">{trackers?.tracker2?.current || 0}%</span>
                </div>
                <div className="h-32">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={trackers?.tracker2?.data || []}>
                      <XAxis dataKey="week" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                      <YAxis hide />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {(trackers?.tracker2?.data || []).map((_, index) => (
                          <Cell key={`cell-${index}`} fill={`hsl(var(--secondary) / ${0.4 + (index * 0.15)})`} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}
    </div>
  );
};

const HomePage = () => {
  const { user, api, isFeatureEnabled, refreshUser } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [trackers, setTrackers] = useState(null);
  const [priorities, setPriorities] = useState([]);
  const [loading, setLoading] = useState(true);

  const needsOnboarding = user?.role !== 'admin' && !user?.is_onboarded;

  // iter164 — Same-session auto-advance from onboarding mode to
  // regular mode. Two mechanisms:
  //
  //   1. Poll `/auth/me` every 8 s WHILE the user is in onboarding.
  //      Picks up admin-side advances without forcing the user to
  //      logout / refresh.
  //   2. When `is_onboarded` flips from false → true within the
  //      current session, show an Apple-style green-checkmark
  //      celebration overlay for ~2.2 s, then render the unlocked
  //      FullDashboard.
  //
  // `wasOnboarding` is a ref-less state that captures the user's
  // gated status at first render so we can detect the EDGE
  // (false→true), not just the steady state.
  const [wasOnboarding, setWasOnboarding] = useState(needsOnboarding);
  const [showCelebration, setShowCelebration] = useState(false);

  useEffect(() => {
    if (!needsOnboarding) return;
    // Poll for admin-side advances.
    const id = setInterval(() => {
      refreshUser?.();
    }, 8000);
    return () => clearInterval(id);
  }, [needsOnboarding, refreshUser]);

  useEffect(() => {
    // Detect false→true transition in-session.
    if (wasOnboarding && !needsOnboarding) {
      setShowCelebration(true);
    }
    // Update the high-water mark so we don't fire again on re-renders.
    if (needsOnboarding && !wasOnboarding) setWasOnboarding(true);
  }, [needsOnboarding, wasOnboarding]);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const [metricsRes, trackersRes, prioritiesRes] = await Promise.all([
          api.get('/dashboard/metrics'),
          api.get('/dashboard/trackers'),
          api.get('/dashboard/priorities')
        ]);
        setMetrics(metricsRes.data);
        setTrackers(trackersRes.data);
        setPriorities(prioritiesRes.data.priorities || []);
      } catch (error) {
        console.error('Dashboard fetch error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, [api]);

  if (loading) {
    return <PageLoader label="Loading..." testId="home-loading" />;
  }

  if (needsOnboarding) {
    return (
      <PullToRefresh testId="home-pull-to-refresh-onboarding">
        <OnboardingDashboard user={user} />
      </PullToRefresh>
    );
  }

  return (
    <PullToRefresh testId="home-pull-to-refresh">
      {showCelebration && (
        <OnboardingCompleteOverlay onDone={() => setShowCelebration(false)} />
      )}
      <FullDashboard user={user} metrics={metrics} trackers={trackers} priorities={priorities} isFeatureEnabled={isFeatureEnabled} />
    </PullToRefresh>
  );
};

export default HomePage;
